import type { ReactNode } from 'react'

import logoSvg from '@/assets/logo.svg'
import { cn } from '@/lib/utils'

export interface AuthScaffoldProps {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  /** A line under the form: the register call-to-action, "Zurück zur Anmeldung". */
  footer?: ReactNode
  /**
   * The mark above the title. The sign-in artboard draws it at 120px, the
   * funnel's register column at 64px; below 576px the big one steps down,
   * because a 120px mark eats a third of a phone screen above the fold.
   */
  logoClassName?: string
  /** The column width the artboards draw: 456 on sign-in, 440 in the funnel. */
  width?: number
  /** Sign-in centres everything; the funnel's register column is left-aligned. */
  align?: 'center' | 'start'
  /**
   * The funnel's right-hand column ("So geht's weiter"). With it the page
   * becomes two columns from `md` up and stacks below that — on a phone the
   * promise belongs under the form, not above it, because the form is what
   * the person came to fill in.
   */
  aside?: ReactNode
}

/**
 * The page every signed-out screen sits on: white, no card.
 *
 * The design lifts things off `ground` with a shadow, and a card on a white
 * page would be lifted off nothing — so the artboards drop the card the
 * previous version of this scaffold drew and let the column stand on the
 * surface itself.
 */
export function AuthScaffold({
  title,
  subtitle,
  children,
  footer,
  logoClassName = 'h-20 w-20 xs:h-[120px] xs:w-[120px]',
  width = 456,
  align = 'center',
  aside,
}: AuthScaffoldProps) {
  const centred = align === 'center'

  const column = (
    <div
      className={cn('flex w-full flex-col', centred ? 'gap-7 text-center' : 'gap-6')}
      style={{ maxWidth: width }}
    >
      <img
        src={logoSvg}
        alt="formalogix"
        className={cn(logoClassName, centred && 'self-center')}
      />
      <div>
        <h1
          className={cn(
            'text-text',
            // The funnel's register title is drawn a step down from the
            // 40px page title so the form below it stays above the fold.
            centred ? 'text-page-title' : 'text-[34px] font-semibold leading-tight',
          )}
        >
          {title}
        </h1>
        {subtitle && <p className="mt-1.5 text-body text-text-muted">{subtitle}</p>}
      </div>
      {children}
      {footer}
    </div>
  )

  if (!aside) {
    return (
      <main className="flex min-h-full flex-col items-center bg-surface px-4 pb-16 pt-10 md:pt-[120px]">
        {column}
      </main>
    )
  }

  return (
    <div className="grid min-h-full grid-cols-1 md:grid-cols-[minmax(0,1fr)_560px]">
      <main className="flex items-center justify-center bg-surface px-4 py-10 md:p-10">
        {column}
      </main>
      {aside}
    </div>
  )
}
